import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CoreTable from "../../coreComponents/Table/Table.tsx";
import RideLogsModal from "../RideLogsModal/index.tsx";
import { getAllRides, getRideLogs, TripStatus } from "../../services/RideService.ts";
import { styled } from '@mui/material/styles';
import { TextField, Box, Button } from '@mui/material';
import { format, isSameDay, parseISO } from 'date-fns';

interface Location {
  latitude: number;
  longitude: number;
}

interface RideData {
  id: number;
  userId: number;
  driverId: number | null;
  otp: string | null;
  source: Location;
  source_address: string;
  destination: Location;
  destination_address: string;
  status: string;
  distance: string;
  pickupDistance: string;
  vehicleType: string;
  duration: number;
  fare: string;
  pickupFare: string;
  dropFare: string;
  tip: number | null;
  created_at: string;
  updated_at: string;
  ride_end_time: string | null;
  fare_changed: boolean;
  original_fare: string | null;
}

const PAGE_SIZE = 10; // Number of items per page

const Rides: React.FC = () => {


  const ClickableSpan = styled('span')(({ theme }) => ({
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline'
    }
  }));

  const columns = [
    {
      id: "id", label: "ID", searchable: false, render: (value: number) => (
        <ClickableSpan onClick={() => fetchRideLogs(value)}>
          {value}
        </ClickableSpan>
      )
    },
    { id: "userId", label: "User ID", searchable: true },
    { id: "driverId", label: "Driver ID", searchable: true },
    {
      id: "created_at",
      label: "Created At",
      searchable: false,
      render: (value: string) => formatDate(value)
    },
    { id: "source_address", label: "Pickup Location", searchable: false },
    { id: "destination_address", label: "Drop Location", searchable: false },
    {
      id: "status", label: "Status", searchable: true, inputType: 'select',
      options: [{ label: 'No Drivers Available', value: TripStatus.NoDriversAvailable },
      { label: 'Processing', value: TripStatus.Processing },
      { label: 'Accepted', value: TripStatus.Accepted },
      { label: 'Driver Cancelled', value: TripStatus.DriverCancelled },
      { label: 'User Cancelled', value: TripStatus.UserCancelled },
      { label: 'Verified', value: TripStatus.Verified },
      { label: 'Completed', value: TripStatus.Completed },
      { label: 'Aborted', value: TripStatus.Aborted }]
    },
    { id: "distance", label: "Distance (km)", searchable: false },
    { id: "vehicleType", label: "Vehicle Type", searchable: false },
    { id: "duration", label: "Duration (min)", searchable: false },
    { id: "fare", label: "Total Fare", searchable: false },
  ];

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [ridesData, setRidesData] = useState<RideData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [driverId, setDriverId] = useState<number | null>(null);
  const [status, setStatus] = useState<TripStatus | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  const [allRidesData, setAllRidesData] = useState<RideData[]>([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedRideLogs, setSelectedRideLogs] = useState<any[]>([]);

  const filterAndSetRidesData = (data: RideData[], date: string | null) => {
    if (date) {
      const filteredData = data.filter(ride => {
        const rideDate = parseISO(ride.created_at);
        const filterDate = new Date(date + 'T00:00:00.000Z');
        return isSameDay(rideDate, filterDate);
      });
      setRidesData(filteredData);
    } else {
      setRidesData(data);
    }
  };

  const fetchRidesData = async () => {
    setLoading(true);
    try {
      const response = await getAllRides(1, 10000, status, driverId, userId);
      
      if (response.data) {
        const newData = response.data.data;
        setAllRidesData(newData);
        filterAndSetRidesData(newData, selectedDate);
      }
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        console.error("Unauthorized access. Redirecting to login...");
        navigate("/login");
      } else {
        setError("Failed to fetch rides");
        console.log(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || null;
    setSelectedDate(value);
  };

  const handleClearDate = () => {
    setSelectedDate(null);
  };

  const handleResetToToday = () => {
    const today = new Date();
    setSelectedDate(today.toISOString().split('T')[0]);
  };

  // Effect for fetching data when filters change
  useEffect(() => {
    fetchRidesData();
  }, [status, driverId, userId]);

  // Effect for handling date changes
  useEffect(() => {
    filterAndSetRidesData(allRidesData, selectedDate);
  }, [selectedDate, allRidesData]);

  const fetchRideLogs = async (rideId: number) => {
    try {
      const data = await getRideLogs(rideId);
      setSelectedRideLogs(data.data);
      setIsLogsModalOpen(true);
    } catch (error) {
      console.error('Error fetching ride logs:', error);
    }
  };

  const handleUpdate = (id: number) => {
    console.log(`Update ride with ID: ${id}`);
  };

  const handleDelete = async (id: number) => {
    // Implement delete functionality if needed
  };

  const handleSearch = (field: string, value: string) => {
    if (field === 'driverId') {
      setDriverId(Number(value));
    } else if (field === 'userId') {
      setUserId(Number(value));
    } else if (field === 'status') {
      setStatus(value as TripStatus);
    }
  };

  const handleClearFilter = (field: string) => {
    if (field === 'driverId') {
      setDriverId(null);
    } else if (field === 'userId') {
      setUserId(null);
    } else if (field === 'status') {
      setStatus(undefined);
    }
  };

  if (loading && ridesData.length === 0) return <div>Loading rides...</div>;

  if (error) {
    return <div>Error: {error}</div>;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getLastThreeWords = (str) => {
    const words = str.split(',').map(word => word.trim());
    return words.slice(-3).join(', ');
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '20px',
        backgroundColor: '#fff',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', gap: 2, minWidth: 300 }}>
          <input
            type="date"
            value={selectedDate || ''}
            onChange={handleDateChange}
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              flex: 1,
              fontSize: '14px',
              height: '36px'
            }}
          />
          <Button 
            variant="outlined"
            onClick={handleResetToToday}
            size="small"
            style={{
              height: '36px',
              minWidth: '100px'
            }}
          >
            Today
          </Button>
          <Button 
            variant="outlined"
            onClick={handleClearDate}
            size="small"
            style={{
              height: '36px',
              minWidth: '100px'
            }}
          >
            Clear
          </Button>
        </Box>
      </div>

      <CoreTable
        columns={columns}
        data={ridesData?.map((ride) => ({
          ...ride,
          created_at: formatDate(ride.created_at),
          driverId: ride.driverId || "Not Assigned",
          status: ride.status.replace(/_/g, " ").toUpperCase(),
          source_address: getLastThreeWords(ride.source_address),
          destination_address: getLastThreeWords(ride.destination_address),
        }))}
        infiniteScroll={false}
        totalCount={ridesData.length}
        onSearch={handleSearch}
        onClearFilter={handleClearFilter}
        showSearch={false}
        activeFilters={{
          driverId: driverId?.toString() || null,
          userId: userId?.toString() || null,
          status: status || null
        }}
      />
      <RideLogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        logs={selectedRideLogs}
      />
    </div>
  );
};

export default Rides;